"use client";

import React, { useState } from "react";
import { Typography, theme, Tooltip } from "antd";
import { DevLogSettingsModal } from "../dev-tools/DevLogSettingsModal";
import { loggers } from "../../utils/logger";

const { Text } = Typography;

// Import build info from repository-committed build-info.json
import buildInfoData from "../../../build-info.json";

// Build info from committed file + environment variables
const BUILD_INFO = {
  version: buildInfoData.version || "0.1.0",
  buildNumber: buildInfoData.buildNumber || "dev-local",
  buildDate: buildInfoData.buildDate || "2025-01-01T00:00:00.000Z",
  gitCommit: buildInfoData.gitCommit || "local",
  gitBranch: buildInfoData.gitBranch || "main",
  environment: import.meta.env.VITE_APP_ENV || "dev", // Environment still from build process
  buildType: buildInfoData.buildType || "development",
  deploymentDate: buildInfoData.deploymentDate || "2025-01-01T00:00:00.000Z",
};

// Check if we're in a dev environment
const isDevEnvironment = (): boolean => {
  const env = BUILD_INFO.environment.toLowerCase();
  return env === 'dev' || env === 'development';
};

// Check if we're in a production environment
const isProductionEnvironment = (): boolean => {
  const env = BUILD_INFO.environment.toLowerCase();
  return env === 'prod' || env === 'production';
};

interface BuildInfoDisplayProps {
  variant?: "header" | "footer" | "badge";
  style?: React.CSSProperties;
}

export const BuildInfoDisplay: React.FC<BuildInfoDisplayProps> = ({ 
  variant = "header", 
  style = {} 
}) => {
  const { token } = theme.useToken();
  const [showLogSettings, setShowLogSettings] = useState(false);
  const showLogControls = !isProductionEnvironment(); // Show log settings on dev AND staging
  
  // Use the logger utility instead of raw console.log
  loggers.build.log('BuildInfoDisplay - BUILD_INFO:', BUILD_INFO);
  loggers.build.log('BuildInfoDisplay - variant:', variant);
  
  // Show build info in all environments for better debugging
  // Note: In production, we still want to see the build information for troubleshooting

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-AU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Australia/Sydney"
      });
    } catch {
      return dateString;
    }
  };

  const baseStyle: React.CSSProperties = {
    fontSize: "11px",
    color: token.colorTextSecondary,
    fontFamily: "monospace",
    lineHeight: 1.2,
    ...style,
  };

  // Clickable wrapper for non-production environments
  const clickableStyle: React.CSSProperties = showLogControls ? {
    cursor: "pointer",
    transition: "opacity 0.2s",
  } : {};

  const handleClick = () => {
    if (showLogControls) {
      setShowLogSettings(true);
    }
  };

  const tooltipText = showLogControls ? "Click to open log settings" : undefined;

  if (variant === "footer") {
    const content = (
      <div 
        style={{ 
        ...baseStyle,
        textAlign: "center",
        marginTop: "8px",
        padding: "4px 0",
        borderTop: `1px solid ${token.colorBorderSecondary}`,
          ...clickableStyle,
        }}
        onClick={handleClick}
      >
        <Text style={{ fontSize: "10px", color: token.colorTextTertiary }}>
          v{BUILD_INFO.version} • Build {BUILD_INFO.buildNumber} • {BUILD_INFO.gitCommit}
          <br />
          {formatDate(BUILD_INFO.deploymentDate)}
        </Text>
      </div>
    );

    return (
      <>
        {showLogControls ? <Tooltip title={tooltipText}>{content}</Tooltip> : content}
        <DevLogSettingsModal open={showLogSettings} onClose={() => setShowLogSettings(false)} />
      </>
    );
  }

  // Badge variant (for TopNav) - hidden in production
  if (variant === "badge") {
    // Don't show build info badge in production
    if (isProductionEnvironment()) {
      return null;
    }
    
    const content = (
      <div
        style={{
          ...baseStyle,
          display: "inline-block",
          padding: style.padding || "2px 8px",
          borderRadius: style.borderRadius || "6px",
          backgroundColor: (style.background as string) || "#f3f4f6",
          ...clickableStyle,
        }}
        onClick={handleClick}
        onMouseEnter={(e) => showLogControls && (e.currentTarget.style.opacity = "0.8")}
        onMouseLeave={(e) => showLogControls && (e.currentTarget.style.opacity = "1")}
      >
        <Text style={{ 
          fontSize: style.fontSize || "11px",
          color: style.color || "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.3px",
          fontFamily: style.fontFamily || "monospace"
        }}>
          {BUILD_INFO.environment} • v{BUILD_INFO.version} • {BUILD_INFO.buildNumber}
          {showLogControls && <span style={{ marginLeft: "4px", fontSize: "10px" }}>⚙️</span>}
        </Text>
      </div>
    );

    return (
      <>
        {showLogControls ? <Tooltip title={tooltipText}>{content}</Tooltip> : content}
        <DevLogSettingsModal open={showLogSettings} onClose={() => setShowLogSettings(false)} />
      </>
    );
  }

  // Header variant (below logo) - only for non-prod environments
  if (isProductionEnvironment()) {
    return null;
  }
  
  const content = (
    <div 
      style={{
      ...baseStyle,
      textAlign: "left",
      marginTop: "2px",
      padding: "1px 4px",
      borderRadius: "3px",
      backgroundColor: style.color ? "rgba(255, 255, 255, 0.1)" : token.colorFillQuaternary,
        ...clickableStyle,
      }}
      onClick={handleClick}
    >
      <Text style={{ 
        fontSize: style.fontSize || "10px",
        color: style.color || token.colorTextTertiary,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        fontFamily: style.fontFamily || "monospace"
      }}>
        {BUILD_INFO.environment} • v{BUILD_INFO.version} • {BUILD_INFO.buildNumber}
        {showLogControls && <span style={{ marginLeft: "4px" }}>⚙️</span>}
      </Text>
    </div>
  );

  return (
    <>
      {showLogControls ? <Tooltip title={tooltipText}>{content}</Tooltip> : content}
      <DevLogSettingsModal open={showLogSettings} onClose={() => setShowLogSettings(false)} />
    </>
  );
};

export default BuildInfoDisplay;
